import { useTheme } from "@mui/styles";
import {
  setLoading,
  setRoute,
  setTitle,
  toggleReportType,
  toggleTransmissionType,
} from "app/store/transmission/slice/transmissionSlice";
import LoadingSpinner from "components/LoadingSpinner";
import PhysicianFilter from "components/pages/transmissions/filter/PhysicianFilter";
import TransmissionModals from "components/pages/transmissions/modal/TransmissionModals";
import TransmissionProcess from "components/pages/transmissions/table/TransmissionProcess";
import TransmissionTable from "components/pages/transmissions/table/TransmissionTable";
import PatchAccessGuard from "pages/patchMonitoring/PatchAccessGuard";
import PatchMonitoringTabs from "pages/patchMonitoring/PatchMonitoringTabs";
import {
  buildPatchTitle,
  patchTabRoute,
  readPatchView,
} from "pages/patchMonitoring/patchMonitoring.config";
import "pages/transmission/Transmission.css";

import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

/* Ambulatory ECG patch worklist. Reuses the transmissions table and modals unchanged —
 * the review, sign and archive stages are the same, so only the query and columns differ.
 *
 * Billing is withheld here; sending a report back a stage is not.
 */
const PatchMonitoringContent = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const role = localStorage.getItem("role");
  const clinicName = localStorage.getItem("clinicName");

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { isLoading, reportType, transmissionType } = useSelector(
    (state) => state.transmission
  );
  const { currentUser } = useSelector((state) => state.auth);
  const { accountsMapper } = useSelector((state) => state.account);

  const [rowSelection, setRowSelection] = useState({});

  // Reset filters hidden on this page; table waits so it doesn't fetch with stale ones.
  const [hiddenFiltersReset, setHiddenFiltersReset] = useState(false);

  useEffect(() => {
    if (reportType !== "all") dispatch(toggleReportType("all"));
    if (transmissionType !== "all") dispatch(toggleTransmissionType("all"));
    setHiddenFiltersReset(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // The URL decides the tab, so a tab left open on the Transmissions page never carries over.
  const view = readPatchView(pathname, localStorage.getItem("userRole"));
  const viewRoute = patchTabRoute(view);

  useEffect(() => {
    if (pathname !== viewRoute) navigate(viewRoute, { replace: true });
  }, [navigate, pathname, viewRoute]);

  const tabStyles = useCallback(
    (currentPage, isDisplayed) => ({
      border: view === currentPage ? "1px solid #E0E0E0" : "none",
      borderBottom:
        view === currentPage ? theme.palette.background.paper : "none",
      display: isDisplayed ? "block" : "none",
      backgroundColor:
        view === currentPage ? theme.palette.background.paper : "transparent",
    }),
    [view, theme]
  );

  useEffect(() => {
    dispatch(setTitle({ title: buildPatchTitle(view, clinicName) }));
    dispatch(setRoute(view));
    dispatch(setLoading(false));
  }, [dispatch, view, clinicName]);

  // Same rule as Transmissions.
  const showPhysicianFilter =
    role === "clinic" &&
    (clinicName !== "Centra" || view === "clinic-review") &&
    !(
      currentUser?.role === "physician" &&
      accountsMapper?.[clinicName]?.settings?.show_only_assigned_reports
    );

  return (
    <>
      <PatchMonitoringTabs
        view={view}
        isLoading={isLoading}
        tabStyles={tabStyles}
      />

      <div style={isLoading ? { overflow: "hidden" } : {}}>
        {isLoading ? (
          <div
            className="trans-table__loading"
            style={{ backgroundColor: theme.palette.background.paper }}
          >
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <TransmissionProcess role={role} />
            <TransmissionModals setRowSelection={setRowSelection} />

            {showPhysicianFilter && <PhysicianFilter />}

            {hiddenFiltersReset && (
              <TransmissionTable
                view={view}
                role={role}
                theme={theme}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                patch
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

const PatchMonitoring = () => (
  <PatchAccessGuard>
    <PatchMonitoringContent />
  </PatchAccessGuard>
);

export default PatchMonitoring;
